import React, { useState, useEffect } from 'react';
import { Button, Spin, Image, Row, Col } from 'antd';
import { FaCube } from 'react-icons/fa';
import { GrEdit } from 'react-icons/gr';
import { LuScan } from 'react-icons/lu';
import { MdDelete } from 'react-icons/md';
import { EnvironmentOutlined, TagOutlined, CameraOutlined } from '@ant-design/icons';
import { signTypes } from '../data/fakeSignals';
import { getElement } from '../data/signalElements';
import '../styles/ply3DTooltip.css'; // Using exact same CSS as 3D tooltip

function TrafficSignTooltip({ sign, onView3D, onEdit, onDelete, onClose, visible, position, isInsidePopup = false }) {
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [streetName, setStreetName] = useState("");
  const [loadingStreet, setLoadingStreet] = useState(false);

  const normalizedType = sign.type
    .replace(/-/g, "-")
    .toLowerCase()
    .replace(/\//g, "_");
  const signType = signTypes[normalizedType] || {};

  // Fetch street name when tooltip becomes visible
  useEffect(() => {
    if (!visible) return;

    const fetchStreet = async () => {
      setLoadingStreet(true);
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${sign.lat}&lon=${sign.lng}`
        );
        const data = await response.json();
        const roadName =
          data.address?.road ||
          data.address?.pedestrian ||
          data.display_name ||
          "Unknown street";
        setStreetName(roadName);
      } catch (error) {
        console.error("Error during reverse geocoding:", error);
        setStreetName("Unknown street");
      } finally {
        setLoadingStreet(false);
      }
    };

    fetchStreet();
  }, [visible, sign.lat, sign.lng]);

  const [previewVisible, setPreviewVisible] = useState(false);

  if (!visible) return null;

  // EXACT SAME STRUCTURE AS PLY-3D-TOOLTIP
  return (
    <div
      className={isInsidePopup ? "" : "ply-3d-tooltip"}
      style={isInsidePopup ? { position: 'relative', width: '310px' } : {
        position: 'absolute',
        left: position ? `${position.x + 15}px` : 0,
        top: position ? `${position.y - 10}px` : 0,
        zIndex: 9000, // Below image preview modal
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="ply-3d-tooltip-content">
        <button
          className="ply-3d-tooltip-close"
          onClick={(e) => {
            e.stopPropagation();
            if (onClose) onClose();
          }}
          aria-label="Close"
        >
          ×
        </button>

        <div className="ply-3d-popup">
          <h3>
            <b>{getElement(sign.type)?.label || sign.description || sign.type.replace(/-/g, " ")}</b>
          </h3>

          <div className="ply-3d-image-container">
            {sign.image_url && !imageError ? (
              <>
                <img
                  src={sign.image_url}
                  alt={sign.description}
                  className="ply-3d-image"
                  onLoad={() => setImageLoading(false)}
                  onError={() => {
                    setImageError(true);
                    setImageLoading(false);
                  }}
                  style={{ opacity: imageLoading ? 0 : 1 }}
                />
                {imageLoading && (
                  <div className="ply-3d-image-loading">
                    <Spin size="large" />
                  </div>
                )}
                <button
                  type="button"
                  className="ply-3d-preview-button"
                  onClick={() => setPreviewVisible(true)}
                  disabled={imageLoading}
                >
                  <LuScan size={18} />
                </button>
                <Image
                  src={sign.image_url}
                  alt={sign.description}
                  preview={{
                    visible: previewVisible,
                    onVisibleChange: (visible) => setPreviewVisible(visible),
                    zIndex: 9600, // Explicit z-index for the preview
                    maskClassName: 'signal-image-preview-mask',
                    wrapClassName: 'signal-image-preview-wrap'
                  }}
                  style={{ display: "none" }}
                />
              </>
            ) : (
              <div className="ply-3d-placeholder">
                {signType.icon ? (
                  <span style={{ fontSize: 40, color: '#A1E430' }}>{signType.icon}</span>
                ) : (
                  <span style={{ fontSize: 40, color: '#A1E430' }}>🚦</span>
                )}
                <p>Traffic Sign</p>
              </div>
            )}
          </div>

          <Row
            justify={"start"}
            align="middle"
            gutter={8}
            style={{ margin: "18px 0" }}
          >
            <Col>
              <EnvironmentOutlined style={{ color: "#A1E430" }} />
            </Col>
            <Col>
              <span>Indirizzo</span>
              <br />
              <b>{loadingStreet ? <Spin size="small" /> : streetName}</b>
            </Col>
          </Row>

          <Row
            justify={"start"}
            align="middle"
            gutter={8}
            style={{ margin: "18px 0" }}
          >
            <Col>
              <EnvironmentOutlined style={{ color: "#A1E430" }} />
            </Col>
            <Col>
              <span>Coordinate</span>
              <br />
              <b>
                {sign.lat.toFixed(6)}, {sign.lng.toFixed(6)}
              </b>
            </Col>
          </Row>

          {sign.description && (
            <Row
              justify={"start"}
              align="middle"
              gutter={8}
              style={{ margin: "18px 0" }}
            >
              <Col>
                <EnvironmentOutlined style={{ color: "#A1E430" }} />
              </Col>
              <Col>
                <span>Descrizione</span>
                <br />
                <b>{sign.description}</b>
              </Col>
            </Row>
          )}

          {sign.support && (
            <Row justify={"start"} align="middle" gutter={8} style={{ margin: "18px 0" }}>
              <Col>
                <TagOutlined style={{ color: "#A1E430" }} />
              </Col>
              <Col>
                <span>Supporto rilevato</span>
                <br />
                <b>{sign.support}</b>
              </Col>
            </Row>
          )}

          {sign.external_id && (
            <Row justify={"start"} align="middle" gutter={8} style={{ margin: "18px 0" }}>
              <Col>
                <CameraOutlined style={{ color: "#A1E430" }} />
              </Col>
              <Col>
                <span>ID detection</span>
                <br />
                <b>{sign.external_id}</b>
              </Col>
            </Row>
          )}
        </div>

        <div style={{ display: 'flex', gap: '10px', marginTop: '12px' }}>
          <Button
            color="green"
            style={{
              flex: 1,
              borderRadius: "25px",
              backgroundColor: "var(--primary-color)",
              color: "white",
            }}
            onClick={() => {
              if (onEdit) {
                onEdit(sign);
              }
            }}
          >
            <GrEdit />
            Modifica
          </Button>

          <Button
            danger
            style={{
              flex: 1,
              borderRadius: "25px",
              backgroundColor: "#ff4d4f",
              color: "white",
              border: "none"
            }}
            onClick={() => {
              if (onDelete) {
                onDelete(sign);
              }
            }}
          >
            <MdDelete />
            Elimina
          </Button>
        </div>
      </div>
    </div>
  );
}

export default TrafficSignTooltip;